import React, { useState, useEffect } from 'react';
import { getSettingsApi } from '../../utils/api';
import { TrendingUp, Clock, Calendar } from 'lucide-react';

const Analytics = ({ showToast }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSettingsApi();
      setSettings(res.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const formatTime = (time) => {
    const [hour, minute] = time.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minute} ${ampm}`;
  };

  const dayColors = {
    'Tuesday': 'bg-green-500',
    'Thursday': 'bg-blue-500',
    'Wednesday': 'text-teal-500',
    'Friday': 'bg-yellow-500',
    'Monday': 'bg-orange-500',
    'Saturday': 'bg-pink-500',
    'Sunday': 'bg-red-500'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Elegant Ticker Strip - Fixed with proper contrast */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white overflow-hidden border-b border-indigo-700">
        <div className="ticker-wrapper py-3">
          <div className="ticker-content flex items-center gap-16 whitespace-nowrap">
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-300"></span>
              <span className="text-indigo-100">Active Campaigns</span>
              <span className="font-semibold text-white">156</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-blue-300"></span>
              <span className="text-indigo-100">Messages Sent</span>
              <span className="font-semibold text-white">1.2M this month</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-pink-300"></span>
              <span className="text-indigo-100">Avg CTR</span>
              <span className="font-semibold text-white">5.2%</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-amber-300"></span>
              <span className="text-indigo-100">Total Revenue</span>
              <span className="font-semibold text-white">₹45.8L</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-teal-300"></span>
              <span className="text-indigo-100">Best Day</span>
              <span className="font-semibold text-white">Tuesday</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-rose-300"></span>
              <span className="text-indigo-100">Peak Time</span>
              <span className="font-semibold text-white">Evening (5-6 PM)</span>
            </span>
            {/* Duplicate for seamless loop */}
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-300"></span>
              <span className="text-indigo-100">Active Campaigns</span>
              <span className="font-semibold text-white">156</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-blue-300"></span>
              <span className="text-indigo-100">Messages Sent</span>
              <span className="font-semibold text-white">1.2M this month</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-pink-300"></span>
              <span className="text-indigo-100">Avg CTR</span>
              <span className="font-semibold text-white">5.2%</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-amber-300"></span>
              <span className="text-indigo-100">Total Revenue</span>
              <span className="font-semibold text-white">₹45.8L</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-teal-300"></span>
              <span className="text-indigo-100">Best Day</span>
              <span className="font-semibold text-white">Tuesday</span>
            </span>
            <span className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-rose-300"></span>
              <span className="text-indigo-100">Peak Time</span>
              <span className="font-semibold text-white">Evening (5-6 PM)</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Days of Week - MOVED TO FIRST */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-all duration-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-green-50 rounded-lg">
              <Calendar className="text-green-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Best Days of the Week
              </h2>
              <p className="text-sm text-slate-500">
                Days ranked by performance
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {(settings?.best_days_order || ['Tuesday', 'Thursday', 'Wednesday', 'Friday', 'Monday', 'Saturday', 'Sunday']).map((day, index) => {
              const rank = index + 1;
              const isTop3 = rank <= 3;
              
              return (
                <div
                  key={day}
                  className={`
                    flex items-center justify-between p-4 rounded-lg transition-all
                    ${isTop3 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className={`
                      flex items-center justify-center w-10 h-10 rounded-full font-bold
                      ${isTop3 ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-700'}
                    `}>
                      #{rank}
                    </div>
                    <div>
                      <p className={`font-semibold ${isTop3 ? 'text-slate-900' : 'text-slate-700'}`}>
                        {day}
                      </p>
                      {isTop3 && (
                        <p className="text-xs text-green-600">
                          {rank === 1 ? '🏆 Best Performance' : rank === 2 ? '🥈 Great Choice' : '🥉 Good Option'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isTop3 && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="text-green-600" size={20} />
                      <span className="text-sm font-medium text-green-600">
                        {rank === 1 ? 'Top' : rank === 2 ? 'High' : 'Good'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              📈 <strong>Insight:</strong> Top 3 days show 25-35% better engagement compared to weekend campaigns
            </p>
          </div>
        </div>

        {/* Best Time to Schedule - MOVED TO SECOND, UPDATED TO 4 SLOTS */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-500/20 rounded-lg">
              <Clock className="text-indigo-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white dark:text-white light:text-slate-900">
                Best Time to Schedule
              </h2>
              <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                Optimal hours for campaign delivery
              </p>
            </div>
          </div>

          {/* 4 Slots Display */}
          <div className="space-y-3">
            <div className="p-4 bg-gradient-to-r border rounded-lg hover:shadow-lg transition-all" style={{ background: 'rgba(50, 180, 172, 0.1)', borderColor: 'rgba(50, 180, 172, 0.3)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white dark:text-white light:text-slate-900">
                    Slot 1: 5–6pm
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                    Early Evening
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-3 py-1 bg-green-500 text-white rounded-full">
                    Top Performer 🏆
                  </span>
                  <Clock style={{ color: '#32B4AC' }} size={20} />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r border rounded-lg hover:shadow-lg transition-all" style={{ background: 'rgba(50, 180, 172, 0.08)', borderColor: 'rgba(50, 180, 172, 0.25)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white dark:text-white light:text-slate-900">
                    Slot 2: 6–7pm
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                    Evening
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-3 py-1 bg-blue-500 text-white rounded-full">
                    Great Choice ⭐
                  </span>
                  <Clock style={{ color: '#32B4AC' }} size={20} />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-pink-500/20 to-red-500/20 border border-pink-500/30 rounded-lg hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white dark:text-white light:text-slate-900">
                    Slot 3: 7–8pm
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                    Late Evening
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-3 py-1 bg-yellow-600 text-white rounded-full">
                    Good Option ✓
                  </span>
                  <Clock className="text-pink-400" size={20} />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-lg hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white dark:text-white light:text-slate-900">
                    Slot 4: 8–9pm
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                    Night
                  </p>
                </div>
                <Clock className="text-blue-400" size={20} />
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-300 dark:text-blue-300 light:text-blue-800">
              💡 <strong>Pro Tip:</strong> Campaigns scheduled during evening hours (6-8pm) typically achieve 40% higher engagement rates
            </p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-6 border rounded-xl p-6 hover:shadow-xl transition-all duration-200" style={{ background: 'rgba(50, 180, 172, 0.05)', borderColor: 'rgba(50, 180, 172, 0.2)' }}>
        <h2 className="text-xl font-semibold text-white dark:text-white light:text-slate-900 mb-4">
          💡 Campaign Recommendations
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <p className="text-white dark:text-white light:text-slate-900 font-medium">Schedule during prime time</p>
                <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">Use the evening slots (6-8pm) for maximum reach</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <p className="text-white dark:text-white light:text-slate-900 font-medium">Choose optimal days</p>
                <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">Tuesday and Thursday consistently show the best performance</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <p className="text-white dark:text-white light:text-slate-900 font-medium">Use 1:1 ratio images</p>
                <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">Square images (1080x1080) have the highest engagement</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <p className="text-white dark:text-white light:text-slate-900 font-medium">Keep messages concise</p>
                <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">Short, clear messages with strong CTAs perform better</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Analytics;
